import React, { useState, useEffect, useRef } from "react";
import { Tab } from "@headlessui/react";
import {
    getAssociatedSectionHeaderId,
    getTabNavButtonClasses,
    getIndicatorClasses,
} from "./tabHelpers";
import "./Tabs.scss";

/**
 * @param {object} props - The component props.
 * @param {string} [props.size="regular"] - The size of the tabs. Possible values: "regular", "small", "large".
 * @param {Function} props.handleClick - The click event handler for the tabs.
 * @param {Array} [props.tabData=[]] - An array of tab data objects.
 * @param {boolean} [props.hasToggledPaymentMethodView=false] - react local state set to true when the user has toggled the payment method view.
 * @returns {React.JSX.Element} The rendered Tabs component.
 */
const Tabs = ({
    size = "regular",
    handleClick = () => {},
    tabData = [],
    hasToggledPaymentMethodView = false,
}) => {
    // maintains previous selection if navigating away from pages and back
    const storedActiveTab = tabData?.findIndex((tab) => tab.active === true);

    // stored active tab === -1 when no tab is active therefore default to the first tab (0)
    const [activeTab, setActiveTab] = useState(
        storedActiveTab !== -1 ? storedActiveTab : 0,
    );

    const ref = useRef(null);
    const conditionalCreditCardRef = (id) => {
        if (id === "credit-tab") {
            return { ref };
        }
        return {};
    };

    useEffect(() => {
        if (hasToggledPaymentMethodView) {
            ref?.current?.focus();
        }
    }, [hasToggledPaymentMethodView]);

    // handles tab switch when data changes from an external source such as gift abandonment
    useEffect(() => {
        if (storedActiveTab !== -1) {
            setActiveTab(storedActiveTab);
        }
    }, [storedActiveTab]);

    // Use the index to update the activeTab value whenever a tab is clicked
    const handleTabClick = (id, index) => {
        setActiveTab(index);
        handleClick(id);
    };

    const handleKeyDown = (e, id, index) => {
        if (
            e.key === "Enter" ||
            e.keyCode === 13 ||
            e.key === " " ||
            e.keyCode === 32
        ) {
            setActiveTab(index);
            handleClick(id);
        }
    };

    // InstantPaymentMethods passes tab panel data (content) via tabData
    const hasContent = tabData?.some((tab) => tab.content);

    return (
        <Tab.Group selectedIndex={activeTab} manual>
            <div className="qg-vendor-tabs tabs">
                <nav className="tabs__nav">
                    <Tab.List
                        aria-labelledby={getAssociatedSectionHeaderId(tabData)}
                    >
                        {tabData?.map((tab, index) => {
                            const { id, label } = tab;
                            return (
                                <Tab
                                    key={id}
                                    aria-live="polite"
                                    onClick={() => handleTabClick(id, index)}
                                    onKeyUp={(e) => handleKeyDown(e, id, index)}
                                    className={getTabNavButtonClasses(
                                        size,
                                        activeTab,
                                        index,
                                    )}
                                    id={id}
                                    aria-controls={`${id}-panel`}
                                    {...conditionalCreditCardRef(id)}
                                >
                                    {label}
                                </Tab>
                            );
                        })}
                    </Tab.List>
                </nav>
                <div className="qg-vendor-tabs__indicator-ui tabs__indicator-ui">
                    <div className="tabs__indicator-bar"></div>
                    <div
                        className={getIndicatorClasses(tabData, activeTab)}
                    ></div>
                </div>
                {hasContent && (
                    <div className="qg-vendor-tabs__content tabs__content">
                        <Tab.Panels>
                            {tabData?.map((tab) => {
                                const { id, content } = tab;
                                return (
                                    <Tab.Panel
                                        key={id}
                                        aria-labelledby={id}
                                        id={`${id}-panel`}
                                    >
                                        <section>{content}</section>
                                    </Tab.Panel>
                                );
                            })}
                        </Tab.Panels>
                    </div>
                )}
            </div>
        </Tab.Group>
    );
};

export default Tabs;
